import { useAuthorizationServiceV1 } from "@rw1/api-services";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useClickOutside } from "../../../hooks/useClickOutside";
import { endOfDay, startOfDay } from "../../../shared/utils/date";
import { CurrentUserLite, OpenWhich } from "../types";
import {
  canRunExport,
  computeUtcRange,
  downloadBlob,
  getAuditBlob,
  makeDownloadName,
} from "../utils/auditExportUtils";
import { monthsAgoBase } from "../utils/csvAndDateUtils";

export const useAuditLogExport = (
  user: CurrentUserLite | undefined,
  company: unknown,
): {
  fromDate: Date | null;
  toDate: Date | null;
  openWhich: OpenWhich;
  canExport: boolean;
  fromBtnRef: React.RefObject<HTMLButtonElement>;
  toBtnRef: React.RefObject<HTMLButtonElement>;
  calendarRef: React.RefObject<HTMLDivElement>;
  setFromDate: (d: Date | null) => void;
  setToDate: (d: Date | null) => void;
  setOpenWhich: React.Dispatch<React.SetStateAction<OpenWhich>>;
  handleFromClick: () => void;
  handleToClick: () => void;
  handleExport: () => Promise<void>;
  today: Date;
  minFrom: Date;
} => {
  const authorizationClient = useAuthorizationServiceV1();
  const { companyId } = useParams();
  const [fromDate, _setFromDate] = useState<Date | null>(null);
  const [toDate, _setToDate] = useState<Date | null>(null);
  const [openWhich, setOpenWhich] = useState<OpenWhich>(null);

  const fromBtnRef = useRef<HTMLButtonElement>(null!);
  const toBtnRef = useRef<HTMLButtonElement>(null!);
  const calendarRef = useRef<HTMLDivElement>(null!);

  // Boundaries
  const today = useMemo(() => endOfDay(new Date()), []);
  const minFrom = useMemo(() => monthsAgoBase(today, 12), [today]);

  const setFromDate = useCallback(
    (d: Date | null) => {
      if (!d) {
        _setFromDate(null);
      } else {
        let next = startOfDay(d);
        if (next < minFrom) next = minFrom;
        if (toDate && next > startOfDay(toDate)) next = startOfDay(toDate);
        if (next > today) next = today;
        _setFromDate(next);
      }
    },
    [minFrom, toDate, today],
  );

  const setToDate = useCallback(
    (d: Date | null) => {
      if (!d) {
        _setToDate(null);
      } else {
        let next = endOfDay(d);
        if (fromDate && next < startOfDay(fromDate)) next = endOfDay(fromDate);
        if (next > today) next = today;
        _setToDate(next);
      }
    },
    [fromDate, today],
  );

  useEffect(() => {
    if (!openWhich) return;
    const html = document.documentElement;
    const prev = html.style.overflow;
    html.style.overflow = "hidden";

    return () => {
      html.style.overflow = prev;
    };
  }, [openWhich]);

  useClickOutside({
    enabled: !!openWhich,
    onClickOutside: () => setOpenWhich(null),
    excludeRefs: [fromBtnRef, toBtnRef, calendarRef],
  });

  const canExport =
    !!fromDate &&
    !!toDate &&
    fromDate.getTime() <= toDate.getTime() &&
    !!companyId;

  const handleFromClick = useCallback(
    () => setOpenWhich((cur) => (cur === "from" ? null : "from")),
    [],
  );
  const handleToClick = useCallback(
    () => setOpenWhich((cur) => (cur === "to" ? null : "to")),
    [],
  );

  const handleExport = useCallback(async () => {
    if (!canRunExport(true, companyId, fromDate, toDate)) return;

    const fd = fromDate!;
    const td = toDate!;

    const { startUtc, endUtc } = computeUtcRange(fd, td);
    const payload = await getAuditBlob(
      authorizationClient,
      startUtc,
      endUtc,
      companyId!,
    );
    if (!payload) return;

    const filename = makeDownloadName(
      company,
      companyId!,
      fd,
      td,
      payload.name,
    );

    downloadBlob(payload.blob, filename);
  }, [authorizationClient, company, fromDate, toDate, companyId]);

  return {
    fromDate,
    toDate,
    openWhich,
    canExport,
    fromBtnRef,
    toBtnRef,
    calendarRef,
    setFromDate,
    setToDate,
    setOpenWhich,
    handleFromClick,
    handleToClick,
    handleExport,
    today,
    minFrom,
  };
};

export type UseAuditLogExportReturn = ReturnType<typeof useAuditLogExport>;
